import React, { useEffect, useState } from "react";
import {
  Container,
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Row,
  Col
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";
import { format } from 'date-fns';
import {
  GetAllUsers,
  UpdateUserStatus,
  ResetUserPassword,
  AssignStoreToUser,
  UnlockUserAccount,
  UpdateUserDetails,
  DeleteUser,
  GetStores
} from "../../requests";

const Users = () => {
  document.title = "Users Management | Susipizza";

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("SUPER_ADMIN"); // SUPER_ADMIN, STORE_ADMIN, DELIVERY_BOY, CUSTOMER

  // Modals state
  const [resetModal, setResetModal] = useState(false);
  const [storeModal, setStoreModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form fields state
  const [newPassword, setNewPassword] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const token = localStorage.getItem("token");

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch all users
      const usersResp = await GetAllUsers(token);
      setUsers(usersResp?.data?.data || usersResp?.data || []);

      // 2. Fetch all stores for display mapping and dropdown
      const storesResp = await GetStores();
      setStores(storesResp?.data?.data || storesResp?.data || []);
    } catch (err) {
      console.error("Failed to load user administration data:", err);
      toast.error("Failed to retrieve users directory.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusToggle = async (user) => {
    const nextStatus = user.status === "DISABLED" ? "ACTIVE" : "DISABLED";
    setLoading(true);
    try {
      await UpdateUserStatus(token, user.userId, nextStatus);
      toast.success(`User account status updated to ${nextStatus}.`);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to toggle user status.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.warn("Password must be at least 4 characters long.");
      return;
    }
    setLoading(true);
    try {
      await ResetUserPassword(token, selectedUser.userId, newPassword);
      toast.success(`Password for user '${selectedUser.name}' reset successfully.`);
      setResetModal(false);
      setNewPassword("");
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset user password.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignStore = async () => {
    setLoading(true);
    try {
      await AssignStoreToUser(token, selectedUser.userId, selectedStoreId);
      toast.success("Store assignment updated successfully.");
      setStoreModal(false);
      setSelectedStoreId("");
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update store assignment.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockAccount = async (user) => {
    setLoading(true);
    try {
      await UnlockUserAccount(token, user.userId);
      toast.success(`Account unlocked and login attempts reset for '${user.name}'.`);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to unlock account.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!editName.trim()) {
      toast.warn("Name cannot be empty.");
      return;
    }
    setLoading(true);
    try {
      await UpdateUserDetails(token, selectedUser.userId, {
        name: editName.trim(),
        phoneNumber: editPhone.trim(),
      });
      toast.success(`Details for '${editName.trim()}' updated successfully.`);
      setEditModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update user details.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await DeleteUser(token, selectedUser.userId);
      toast.success(`'${selectedUser.name}' has been deleted.`);
      setDeleteModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to delete user.");
    } finally {
      setLoading(false);
    }
  };

  // Helper to resolve store name
  const getStoreName = (storeId) => {
    if (!storeId) return "Unassigned";
    const store = stores.find((s) => s.storeId === storeId);
    return store ? store.name : storeId;
  };

  // Filter users by role and search term
  const filteredUsers = users.filter((u) => {
    const matchesRole = u.role === activeTab;
    if (!matchesRole) return false;

    const query = searchTerm.toLowerCase();
    const nameMatch = (u.name || "").toLowerCase().includes(query);
    const emailMatch = (u.email || "").toLowerCase().includes(query);
    const phoneMatch = (u.phoneNumber || "").toLowerCase().includes(query);
    const usernameMatch = (u.username || "").toLowerCase().includes(query);

    return nameMatch || emailMatch || phoneMatch || usernameMatch;
  });

  const totalRows = filteredUsers.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const renderDate = (dateVal) => {
    if (!dateVal) return "Never";
    try {
      return format(new Date(dateVal), "PPpp");
    } catch (_) {
      return String(dateVal);
    }
  };

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Users Control Room" />

          {/* Role Tabs */}
          <Nav tabs className="nav-tabs-custom justify-content-start mb-4">
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "SUPER_ADMIN" })}
                onClick={() => { setActiveTab("SUPER_ADMIN"); setSearchTerm(""); }}
              >
                Super Admins
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "STORE_ADMIN" })}
                onClick={() => { setActiveTab("STORE_ADMIN"); setSearchTerm(""); }}
              >
                Store Admins
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "DELIVERY_BOY" })}
                onClick={() => { setActiveTab("DELIVERY_BOY"); setSearchTerm(""); }}
              >
                Delivery Riders
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "CUSTOMER" })}
                onClick={() => { setActiveTab("CUSTOMER"); setSearchTerm(""); }}
              >
                Customers (Read-only)
              </NavLink>
            </NavItem>
          </Nav>

          {/* Search bar */}
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <div style={{ width: "320px" }}>
              <Input
                type="text"
                placeholder={`Search by Name, Email, Username, Phone...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="text-muted small">
              Total {activeTab.replace("_", " ").toLowerCase()}(s): {filteredUsers.length}
            </div>
          </div>

          {/* Table Container */}
          <div className="table-responsive bg-white rounded shadow-sm">
            <table className="table align-middle table-nowrap mb-0 table-hover">
              <thead className="table-light">
                <tr>
                  <th>SR No.</th>
                  <th>Name</th>
                  <th>Email/Username</th>
                  <th>Phone Number</th>
                  {(activeTab === "STORE_ADMIN" || activeTab === "DELIVERY_BOY") && <th>Store Assignment</th>}
                  <th>Last Login</th>
                  <th>Status</th>
                  {activeTab !== "CUSTOMER" && <th className="text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      No accounts found matching search criteria.
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user, idx) => (
                    <tr key={user.userId || user.id}>
                      <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                      <td>
                        <div className="font-size-14 font-weight-bold">{user.name}</div>
                        {user.isFirstLogin && (
                          <Badge color="warning" pill className="me-1">
                            Pending Password Setup
                          </Badge>
                        )}
                      </td>
                      <td>{user.email || user.username}</td>
                      <td>{user.phoneNumber || "N/A"}</td>
                      {(activeTab === "STORE_ADMIN" || activeTab === "DELIVERY_BOY") && (
                        <td>
                          <Badge color="light" className="text-dark border font-size-12 px-2 py-1">
                            {getStoreName(user.storeId)}
                          </Badge>
                        </td>
                      )}
                      <td>{renderDate(user.lastLogin)}</td>
                      <td>
                        <Badge
                          color={user.status === "DISABLED" ? "danger" : "success"}
                          pill
                          style={{ padding: "5px 12px", cursor: activeTab !== "CUSTOMER" ? "pointer" : "default" }}
                          onClick={() => activeTab !== "CUSTOMER" && handleStatusToggle(user)}
                        >
                          {user.status === "DISABLED" ? "🚫 Disabled" : "🟢 Active"}
                        </Badge>
                      </td>
                      {activeTab !== "CUSTOMER" && (
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              color="light"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setEditName(user.name || "");
                                setEditPhone(user.phoneNumber || "");
                                setEditModal(true);
                              }}
                              title="Edit Name/Phone"
                            >
                              <i className="mdi mdi-pencil me-1 text-secondary"></i> Edit
                            </Button>

                            <Button
                              color="light"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setResetModal(true);
                              }}
                              title="Reset Password"
                            >
                              <i className="mdi mdi-key me-1 text-primary"></i> Pass
                            </Button>

                            {(activeTab === "STORE_ADMIN" || activeTab === "DELIVERY_BOY") && (
                              <Button
                                color="light"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setSelectedStoreId(user.storeId || "");
                                  setStoreModal(true);
                                }}
                                title="Change Store"
                              >
                                <i className="mdi mdi-store me-1 text-info"></i> Store
                              </Button>
                            )}

                            {(user.failedAttempts || 0) >= 5 && (
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => handleUnlockAccount(user)}
                                title="Unlock Account"
                              >
                                <i className="mdi mdi-lock-open me-1"></i> Unlock
                              </Button>
                            )}

                            <Button
                              color="light"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteModal(true);
                              }}
                              title="Delete Account"
                            >
                              <i className="mdi mdi-delete me-1 text-danger"></i> Delete
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', background: 'white', borderTop: '1px solid #e9ecef' }} className="rounded-bottom">
                <span className="text-muted small">
                  Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows} entries
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      {/* Password Reset Modal */}
      <Modal isOpen={resetModal} toggle={() => setResetModal(!resetModal)} centered>
        <ModalHeader toggle={() => setResetModal(!resetModal)}>
          Reset Password — {selectedUser?.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="passwordInput">Enter New Password</Label>
            <Input
              type="password"
              id="passwordInput"
              placeholder="Min 4 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => { setResetModal(false); setNewPassword(""); }}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleResetPassword}>
            Save Password
          </Button>
        </ModalFooter>
      </Modal>

      {/* Store Assignment Modal */}
      <Modal isOpen={storeModal} toggle={() => setStoreModal(!storeModal)} centered>
        <ModalHeader toggle={() => setStoreModal(!storeModal)}>
          Assign Store — {selectedUser?.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="storeSelect">Select Assigned Store</Label>
            <Input
              type="select"
              id="storeSelect"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
            >
              <option value="">Unassigned / No Store</option>
              {stores.map((s) => (
                <option key={s.id} value={s.storeId}>
                  {s.name} ({s.storeId})
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setStoreModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleAssignStore}>
            Assign Store
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Name/Phone Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(!editModal)} centered>
        <ModalHeader toggle={() => setEditModal(!editModal)}>
          Edit — {selectedUser?.name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="editNameInput">Name</Label>
            <Input
              type="text"
              id="editNameInput"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="editPhoneInput">Phone Number</Label>
            <Input
              type="text"
              id="editPhoneInput"
              value={editPhone}
              onChange={(e) => setEditPhone(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setEditModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleEditUser}>
            Save Changes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>
          Delete Account
        </ModalHeader>
        <ModalBody>
          Are you sure you want to permanently delete{" "}
          <strong>{selectedUser?.name}</strong> ({selectedUser?.email || selectedUser?.username})?
          This cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteUser}>
            Delete Permanently
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Users;